// components/UpdateComponents/ConnectedUsersList.tsx
import ChatIcon from '@mui/icons-material/Chat'
import CloseIcon from '@mui/icons-material/Close'
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Modal,
  Typography,
  useTheme,
} from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import { ROLES } from 'config/roles.config'
import React, { useEffect, useMemo, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useQuickChat } from '../../context/QuickChat'
import SendBroadcast from '../../context/Websocket/SendBroadcast'
import { useWebsocketContext } from '../../context/Websocket/WebsocketProvider'

const adminUsers = ROLES.ADMIN_IDS

const formatElapsed = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

const ConnectedUsersListContent: React.FC<{
  users: any[]
  isAdmin?: boolean
  onChatWithUser?: (userId: string) => void
  currentUserId?: string
}> = ({ users, isAdmin, onChatWithUser, currentUserId }) => {
  const { connectedUsers } = useWebsocketContext()
  // tick every second to update "connected for" labels
  const [, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Merge Slack users (avatars/names) with WS connections by id
  const merged = useMemo(() => {
    return (connectedUsers ?? [])
      .map(cu => {
        const slack = users?.find((u: any) => u.user_uuid === cu.id)
        return {
          id: cu.id,
          name:
            slack?.name ||
            slack?.real_name ||
            cu.name ||
            `${cu.firstName ?? ''} ${cu.lastName ?? ''}`.trim() ||
            'Unknown',
          image_512: slack?.image_512 || slack?.profile?.image_512 || slack?.profile?.image_192 || '',
          connectedAt: Number(cu.connectedAt), // 👈 ensure it's a number
        }
      })
      .sort((a, b) => a.connectedAt - b.connectedAt)
  }, [connectedUsers, users])

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="subtitle2" sx={{ opacity: 0.75 }}>
          Currently connected:
        </Typography>
        <Badge color="primary" sx={{ ml: 1 }} badgeContent={merged?.length || 0} />
      </Box>

      <List>
        {merged && merged.length > 0 ? (
          merged.map(u => {
            const isSelf = u.id === currentUserId
            return (
              <React.Fragment key={u?.id}>
                <ListItem
                  key={u.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    borderRadius: '6px',
                    cursor: !isSelf && onChatWithUser ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                    '&:hover': !isSelf && onChatWithUser ? { backgroundColor: 'action.hover' } : {},
                  }}
                  onClick={() => !isSelf && onChatWithUser?.(u.id)}
                >
                  <Avatar src={u.image_512} sx={{ mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {u.name}
                      {isSelf && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.5 }}>
                          (you)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Connected for: {formatElapsed(Date.now() - u.connectedAt)}
                    </Typography>
                  </Box>
                  {!isSelf && onChatWithUser && (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        onChatWithUser(u.id)
                      }}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
              </React.Fragment>
            )
          })
        ) : (
          <ListItem>
            <Typography variant="body2">No users connected</Typography>
          </ListItem>
        )}
      </List>
    </>
  )
}

const ConnectedUsersList: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const theme = useTheme()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { activeUser } = useActiveUser()
  const { setDrawerOpen, setActiveConversation } = useQuickChat()
  const currentUserId = activeUser?.user?.id ?? ''

  const isAdmin = useMemo(() => {
    if (!activeUser || isUsersLoading || !users) return false
    return (activeUser?.user && adminUsers.includes(activeUser.user.id)) || false
  }, [activeUser])

  const handleChatWithUser = (userId: string) => {
    setActiveConversation(userId)
    setDrawerOpen(true)
    onClose()
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        <BoxScroller
          sx={{
            position: 'absolute',
            top: '39%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            height: '100%',
            maxHeight: isAdmin ? '65vh' : '85vh',
            overflowY: 'auto',
            bgcolor: theme.palette.background.paper,
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: 'none',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Connected Users</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {isUsersLoading ? (
            <CircularProgress />
          ) : (
            <ConnectedUsersListContent
              users={users || []}
              isAdmin={isAdmin}
              onChatWithUser={handleChatWithUser}
              currentUserId={currentUserId}
            />
          )}
        </BoxScroller>
        {isAdmin && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '0%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 520,
              overflowY: 'auto',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              outline: 'none',
              backdropFilter: 'blur(10px)',
              mt: theme.spacing(2),
            }}
          >
            <SendBroadcast />
          </Box>
        )}
      </>
    </Modal>
  )
}

export default ConnectedUsersList
